import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { useFormStepper } from './FormStepperContext';

/**
 * FormStep - A standardized component for form step content
 * 
 * This component provides consistent styling and behavior for step content
 * with standard header, description, and validation error messaging.
 * 
 * @param {Object} props - Component properties
 * @param {string} props.label - Step label
 * @param {string} props.description - Step description
 * @param {React.ReactNode} props.icon - Step icon
 * @param {React.ReactNode} props.children - Step content
 * @param {boolean} props.showSummary - Whether to show step summary (default: true)
 * @param {Function} props.validateStep - Function to validate the step
 * @param {Object} props.sx - Additional styles
 */
const FormStep = ({
  label,
  description,
  icon,
  children,
  showSummary = true,
  validateStep,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const { validationError, activeStep } = useFormStepper();
  
  return (
    <Box
      sx={{
        position: 'relative',
        opacity: 1,
        transform: 'translateY(0)',
        transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-out',
        ...sx
      }}
      {...props}
    >
      {/* Validation Error Message */}
      {validationError && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 1,
            backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.1 : 0.05),
            borderLeft: (theme) => `4px solid ${theme.palette.error.main}`,
          }}
        >
          <Typography
            color="error"
            variant="body2"
            sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}
          >
            <Box
              component="span"
              sx={{
                mr: 1,
                display: 'inline-flex',
                '& svg': {
                  fontSize: 20,
                }
              }}
            >
              ⚠️
            </Box>
            Please correct the errors in this step before proceeding.
          </Typography>
        </Box>
      )}

      {/* Step Summary */}
      {showSummary && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: (theme) => alpha(theme.palette.text.primary, 0.9),
              mb: 1,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {icon && (
              <Box component="span" sx={{ mr: 1, display: 'flex', color: 'primary.main' }}>
                {icon}
              </Box>
            )}
            {label}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      )}

      {/* Step Content */}
      <Box>
        {children}
      </Box>
    </Box>
  );
};

export default FormStep;